import React, { useState } from 'react';
import AuthPageShell, { AuthFormHeading, AuthRule } from './AuthPageShell';
import Button from '../ui/Button';
import Input from '../ui/Input';

interface NewPasswordFormProps {
    email: string;
    onSubmit: (newPassword: string) => Promise<void>;
}

// The password-reset link's form. AuthActionPage owns the reset code and
// passes in the account's email and the submit action.
function NewPasswordForm({ email, onSubmit }: NewPasswordFormProps) {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!password || !confirmPassword) {
            setError('Please fill in all fields');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            await onSubmit(password);
        } catch (err: any) {
            setError(err.message || 'Failed to set new password');
            setIsLoading(false);
        }
    };

    return (
        <AuthPageShell>
            <AuthFormHeading title="Set New Password" />

            <p className="mt-5 text-lead-sm font-semibold text-[#6E6E6E]">
                Choose a new password for <span className="text-ink-secondary">{email}</span>.
            </p>

            <form onSubmit={handleSubmit} className="mt-2 flex flex-col">
                <Input
                    variant="line"
                    type="password"
                    placeholder="New password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                />
                <Input
                    variant="line"
                    type="password"
                    placeholder="Confirm new password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={isLoading}
                />

                {error && <p className="mt-4 text-meta font-semibold text-alert">{error}</p>}

                <Button type="submit" variant="primary" size="lg" fullWidth className="mt-6" disabled={isLoading}>
                    {isLoading ? 'Saving…' : 'Save Password'}
                </Button>
            </form>

            <AuthRule className="mt-7" />

            <a
                href="/"
                className="mt-[18px] self-start text-[13px] text-[#6E6E6E] underline transition-colors hover:text-ink-secondary"
            >
                Back to Log In
            </a>
        </AuthPageShell>
    );
}

export default NewPasswordForm;
